import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { DEPOSIT_TYPES, PLAN_STATUSES, REPAYMENT_FREQUENCIES } from '@solar/contracts';

import { Alert } from '../../../components/Alert';
import { Button } from '../../../components/Button';
import { FormField } from '../../../components/forms/FormField';
import { PageSpinner } from '../../../components/PageSpinner';
import { useSuccessNotification } from '../../../components/SuccessNotification';
import { catalogueApi } from '../../../features/catalogue/catalogue.api';
import { toDisplayMessage } from '../../../lib/error-message';
import { formatStatusLabel } from '../../../lib/format-status';
import { routeTo } from '../../../routes/paths';

import styles from './AdminPortal.module.css';

export function AdminRepaymentPlanFormPage(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const isEdit = id !== undefined && id !== 'new';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { notify, dismiss } = useSuccessNotification();

  const plan = useQuery({
    queryKey: ['admin', 'repayment-plans', id],
    queryFn: () => catalogueApi.getRepaymentPlan(id ?? ''),
    enabled: isEdit,
  });

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [depositType, setDepositType] = useState<string>(DEPOSIT_TYPES[1]);
  const [depositValue, setDepositValue] = useState('20.00');
  const [tenureMonths, setTenureMonths] = useState('12');
  const [minProductPrice, setMinProductPrice] = useState('');
  const [maxProductPrice, setMaxProductPrice] = useState('');
  const [repaymentFrequency, setRepaymentFrequency] = useState<string>(REPAYMENT_FREQUENCIES[0]);
  const [status, setStatus] = useState<string>(PLAN_STATUSES[0]);

  useEffect(() => {
    if (plan.data !== undefined) {
      setCode(plan.data.code);
      setName(plan.data.name);
      setDescription(plan.data.description ?? '');
      setDepositType(plan.data.depositType);
      setDepositValue(plan.data.depositValue);
      setTenureMonths(String(plan.data.tenureMonths));
      setMinProductPrice(plan.data.minProductPrice ?? '');
      setMaxProductPrice(plan.data.maxProductPrice ?? '');
      setRepaymentFrequency(plan.data.repaymentFrequency);
      setStatus(plan.data.status);
    }
  }, [plan.data]);

  const save = useMutation({
    mutationFn: () => {
      if (isEdit) {
        return catalogueApi.updateRepaymentPlan(id ?? '', {
          name,
          description: description === '' ? undefined : description,
          depositType,
          depositValue,
          tenureMonths: Number.parseInt(tenureMonths, 10),
          minProductPrice: minProductPrice === '' ? undefined : minProductPrice,
          maxProductPrice: maxProductPrice === '' ? undefined : maxProductPrice,
          status,
        });
      }

      return catalogueApi.createRepaymentPlan({
        code,
        name,
        description: description === '' ? undefined : description,
        depositType,
        depositValue,
        tenureMonths: Number.parseInt(tenureMonths, 10),
        minProductPrice: minProductPrice === '' ? undefined : minProductPrice,
        maxProductPrice: maxProductPrice === '' ? undefined : maxProductPrice,
        repaymentFrequency,
      });
    },
    onMutate: () => {
      dismiss();
    },
    onSuccess: (saved) => {
      notify(isEdit ? 'Repayment plan updated.' : 'Repayment plan created.');
      void queryClient.invalidateQueries({ queryKey: ['admin', 'repayment-plans'] });
      void navigate(routeTo.adminRepaymentPlan(saved.id));
    },
  });

  if (isEdit && plan.isPending) {
    return <PageSpinner label="Loading plan" />;
  }

  if (isEdit && plan.isError) {
    return (
      <Alert tone="error">
        {toDisplayMessage(plan.error)}{' '}
        <Button variant="secondary" onClick={() => void plan.refetch()}>
          Retry
        </Button>
      </Alert>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <p className={styles.eyebrow}>Catalogue management</p>
          <h1 className={styles.pageTitle}>{isEdit ? 'Edit plan' : 'Create plan'}</h1>
          <p className={styles.pageSubtitle}>
            Configure the server-backed commercial template without implying unsupported interest
            rules.
          </p>
        </div>
      </header>

      <section className={styles.card} aria-labelledby="plan-form-heading">
        <h2 className={styles.cardTitle} id="plan-form-heading">
          Template
        </h2>

        {save.isError && <Alert tone="error">{toDisplayMessage(save.error)}</Alert>}

        <form
          className={styles.filters}
          onSubmit={(event) => {
            event.preventDefault();
            save.mutate();
          }}
        >
          {!isEdit && (
            <FormField
              label="Code"
              name="code"
              value={code}
              required
              onChange={(e) => setCode(e.target.value)}
            />
          )}
          <FormField
            label="Name"
            name="name"
            value={name}
            required
            onChange={(e) => setName(e.target.value)}
          />
          {!isEdit && (
            <label className={styles.filterLabel} htmlFor="repayment-frequency">
              Repayment frequency
              <select
                id="repayment-frequency"
                value={repaymentFrequency}
                onChange={(event) => setRepaymentFrequency(event.target.value)}
              >
                {REPAYMENT_FREQUENCIES.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
          )}
          {isEdit && (
            <label className={styles.filterLabel} htmlFor="plan-edit-status">
              Status
              <select
                id="plan-edit-status"
                value={status}
                onChange={(event) => setStatus(event.target.value)}
              >
                {PLAN_STATUSES.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
          )}
          {isEdit && plan.isSuccess && (
            <p className={styles.notice}>
              Calculation method: {formatStatusLabel(plan.data.calculationMethod)}. Frequency:{' '}
              {formatStatusLabel(plan.data.repaymentFrequency)}. These fields are fixed by the
              current backend contract.
            </p>
          )}
          <FormField
            label="Description"
            name="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <label className={styles.checkboxRow}>
            Deposit type
            <select value={depositType} onChange={(e) => setDepositType(e.target.value)}>
              {DEPOSIT_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </label>
          <FormField
            label="Deposit value"
            name="depositValue"
            value={depositValue}
            required
            onChange={(e) => setDepositValue(e.target.value)}
          />
          <FormField
            label="Tenure (months)"
            name="tenureMonths"
            type="number"
            value={tenureMonths}
            required
            onChange={(e) => setTenureMonths(e.target.value)}
          />
          <FormField
            label="Min product price"
            name="minProductPrice"
            value={minProductPrice}
            onChange={(e) => setMinProductPrice(e.target.value)}
          />
          <FormField
            label="Max product price"
            name="maxProductPrice"
            value={maxProductPrice}
            onChange={(e) => setMaxProductPrice(e.target.value)}
          />
          <Button type="submit" disabled={save.isPending}>
            Save plan
          </Button>
        </form>
      </section>
    </div>
  );
}
